/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState, useRef } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { getCSS, getCSSVariableValue } from '../../../../assets/ts/_utils'
import { useThemeMode } from '../../../layout/theme-mode/ThemeModeProvider'
import { useIntl } from 'react-intl'
import { useAuth } from '../../../../../app/modules/auth'
import { getCurriculumStudyChart } from '../../../../../app/modules/curriculum/curricula-list/core/_requests'
import { ChartModel, initialChartModel } from '../../../../../_metronic/partials/widgets/charts/_models'
import { useYear } from '../../../../../_metronic/partials/layout/header-menus/YearsManager'
import { RoleModel } from '../../../../../app/modules/auth/core/_models'
import { useTerm } from '../../../layout/header-menus/TermsManager'

type Props = {
  className: string
}

const CurriculumStudyChart: React.FC<Props> = ({ className }) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()
  const intl = useIntl()
  const { currentUser, auth } = useAuth()
  const currentYearId = useYear()
  const currentTermId = useTerm()

  const [curriculumStudyChart, setCurriculumStudyChart] = useState<ChartModel | undefined>(initialChartModel)

  useEffect(() => {
    let query = ''
    if (auth?.currentRole === RoleModel.Admin)
      query = '&academicYearId=' + currentYearId
    else if (auth?.currentRole === RoleModel.InstitutionManager)
      query = '&institutionId=' + currentUser?.institutionId + '&academicYearId=' + currentYearId
    else if (auth?.currentRole === RoleModel.Customer || auth?.currentRole === RoleModel.Person)
      query = '&customerId=' + currentUser?.customerId + '&academicYearId=' + currentYearId
    else
      query =
        '&institutionId=' +
        currentUser?.institutionId +
        '&subinstitutionId=' +
        currentUser?.subinstitutionId +
        '&academicYearId=' +
        currentYearId +
        '&termId=' +
        currentTermId +
        '&employeeId=' +
        currentUser?.employeeId

    getCurriculumStudyChart(query).then((value) => {
      setCurriculumStudyChart(value)
    })
  }, [])

  const refreshMode = () => {
    if (!chartRef.current) {
      return
    }
    const height = parseInt(getCSS(chartRef.current, 'height'))
    const chart = new ApexCharts(chartRef.current, getChartOptions(height))

    if (chart) {
      chart.render()
    }

    return chart
  }

  useEffect(() => {
    const chart = refreshMode()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, mode, curriculumStudyChart])

  function getChartOptions(height: number): ApexOptions {
    const labelColor = getCSSVariableValue('--kt-gray-500')
    const borderColor = getCSSVariableValue('--kt-warning')

    return {
      series: [
        {
          data: curriculumStudyChart?.series1!.map((serie, index) => ({
            x: curriculumStudyChart?.categories![index], 
            y: (serie / 60).toFixed(2)
          })) ?? [{ x: '', y: 0 }]        }
      ],
        legend: {
        show: false
      },
      chart: {
        height: 350,
        type: 'treemap'
      },
      stroke: {
        width: 3,
        colors: ['#2986cc']
      },
      colors: [
        '#3B93A5',
        '#F7B844',
        '#ADD8C7',
        '#EC3C65',
        '#CDD7B6',
        '#C1F666',
        '#D43F97',
        '#1E5D8C',
        '#421243',
        '#7F94B0',
        '#EF6537',
        '#C0ADDB'
      ],
      plotOptions: {
        treemap: {
          distributed: true,
          enableShades: false
        }
      }
    }
  }

  return (
    <div className={`card ${className}`}>
      {/* begin::Header */}
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>{intl.formatMessage({ id: '6ab' })}</span>
          <span className='text-muted fw-semibold fs-7'>{intl.formatMessage({ id: '6ac' })}</span>
        </h3>
      </div>
      {/* end::Header */}

      {/* begin::Body */}
      <div className='card-body'>
        {/* begin::Chart */}
        <div ref={chartRef} id='kt_charts_widget_3_chart' style={{ height: '350px' }}></div>
        {/* end::Chart */}
      </div>
      {/* end::Body */}
    </div>
  )
}

export { CurriculumStudyChart }
